import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trophy, Star, Shield, Bug, Wrench, TrendingUp, Crown, Medal, ChevronUp, ChevronDown } from "lucide-react";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const ReputationStars = ({ level = 0, size = "sm" }) => {
  const sizeClass = size === "sm" ? "w-3 h-3" : "w-4 h-4";
  return (
    <div className="flex gap-0.5" data-testid="reputation-stars">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${sizeClass} ${
            i <= level ? "text-amber-400 fill-amber-400" : "text-muted-foreground/30"
          }`}
        />
      ))}
    </div>
  );
};

const RankBadge = ({ rank }) => {
  if (rank === 1) return <Crown className="w-5 h-5 text-amber-400" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-slate-300" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-amber-700" />;
  return <span className="text-xs font-mono text-muted-foreground w-5 text-center">#{rank}</span>;
};

export { ReputationStars };

export default function Leaderboard() {
  const { user } = useAuth();
  const [leaders, setLeaders] = useState([]);
  const [period, setPeriod] = useState("weekly");
  const [loading, setLoading] = useState(true);
  const [myScore, setMyScore] = useState(null);
  const [weekStart, setWeekStart] = useState("");

  useEffect(() => {
    fetchLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    if (user?.user_id) {
      fetchMyScore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/leaderboard`, { params: { period, limit: 50 } });
      setLeaders(res.data.leaders || []);
      setWeekStart(res.data.week_start || "");
    } catch (err) {
      console.error("Leaderboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyScore = async () => {
    try {
      const res = await axios.get(`${API}/users/${user.user_id}/score`);
      setMyScore(res.data);
    } catch (err) {
      console.error("Score fetch error:", err);
    }
  };

  const scoreField = period === "weekly" ? "weekly_score" : "all_time_score";

  return (
    <div className="p-4 md:p-8 animate-fade-in" data-testid="leaderboard-page">
      <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-2">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-widest text-foreground flex items-center gap-3">
            <Trophy className="w-7 h-7 text-amber-400" />
            Leaderboard
          </h1>
          <div className="cyber-line mt-2" />
          <p className="text-sm text-muted-foreground font-mono mt-2">
            {period === "weekly" && weekStart ? `Week of ${new Date(weekStart).toLocaleDateString()}` : "All-time rankings"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={period === "weekly" ? "default" : "outline"}
            size="sm"
            onClick={() => setPeriod("weekly")}
            data-testid="leaderboard-weekly-btn"
          >
            Weekly
          </Button>
          <Button
            variant={period === "all_time" ? "default" : "outline"}
            size="sm"
            onClick={() => setPeriod("all_time")}
            data-testid="leaderboard-alltime-btn"
          >
            All-Time
          </Button>
        </div>
      </div>

      {/* My Stats Card */}
      {myScore && (
        <Card className="border-primary/30 bg-primary/5" data-testid="my-score-card">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground font-mono uppercase">Your Score</p>
                  <p className="text-lg font-bold font-mono">{myScore[scoreField] || 0}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Bug className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground font-mono uppercase">Reported</p>
                  <p className="text-sm font-mono">{myScore.bugs_reported || 0}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <div>
                  <p className="text-xs text-muted-foreground font-mono uppercase">Verified</p>
                  <p className="text-sm font-mono">{myScore.bugs_verified || 0}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Wrench className="w-4 h-4 text-blue-400" />
                <div>
                  <p className="text-xs text-muted-foreground font-mono uppercase">Fixed</p>
                  <p className="text-sm font-mono">{myScore.bugs_fixed || 0}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-mono uppercase mb-1">Reputation</p>
                <ReputationStars level={myScore.reputation_level || 0} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard Table */}
      <Card data-testid="leaderboard-table">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-400" />
            Top Contributors ({period === "weekly" ? "This Week" : "All Time"})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground font-mono text-sm">Loading...</div>
          ) : leaders.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground font-mono text-sm">
              No scores yet. Start reporting bugs to earn points!
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {leaders.map((leader, idx) => (
                <div
                  key={leader.user_id}
                  className={`flex items-center gap-4 px-4 py-3 ${
                    idx < 3 ? "bg-amber-400/5" : ""
                  } ${leader.user_id === user?.user_id ? "ring-1 ring-primary/40 bg-primary/5" : ""}`}
                  data-testid={`leaderboard-row-${idx}`}
                >
                  <div className="w-8 flex justify-center">
                    <RankBadge rank={leader.rank} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {leader.name || leader.email || "Anonymous"}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <ReputationStars level={leader.reputation_level || 0} />
                      <span className="text-xs text-muted-foreground font-mono">
                        {leader.bugs_verified || 0} verified
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold font-mono">{leader[scoreField] || 0}</p>
                    <p className="text-xs text-muted-foreground font-mono">pts</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scoring Info */}
      <Card className="bg-card/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading uppercase tracking-wider text-muted-foreground">
            How Scoring Works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs font-mono">
            <div className="flex items-start gap-2">
              <ChevronUp className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-foreground font-bold">+10 pts</p>
                <p className="text-muted-foreground">Bug reaches Verified</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <ChevronUp className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-foreground font-bold">+25 pts</p>
                <p className="text-muted-foreground">Bug reaches Fixed (first reporter)</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <ChevronUp className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-foreground font-bold">+5 pts</p>
                <p className="text-muted-foreground">Duplicate confirms a bug</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <ChevronUp className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-foreground font-bold">+5/+10 bonus</p>
                <p className="text-muted-foreground">High-impact bugs (5+/10+ duplicates)</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
